import React, { useState, useEffect, useRef } from 'react';
import { X, Check, Lock, Zap } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { getUserPlan } from '../services/userService';
import { redirectToCheckout } from '../services/stripeService';

const UpgradeModal = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const [currentPlan, setCurrentPlan] = useState('free');
  const [loadingPlanId, setLoadingPlanId] = useState(null); // null, 'pro', or 'growth'
  const [error, setError] = useState('');
  const isProcessingRef = useRef(false); // Prevent double clicks

  useEffect(() => {
    if (isOpen && currentUser?.uid) {
      loadCurrentPlan();
    }
  }, [isOpen, currentUser]);

  const loadCurrentPlan = async () => {
    try {
      const plan = await getUserPlan(currentUser.uid);
      setCurrentPlan(plan);
    } catch (err) {
      console.error('Error loading plan:', err);
      setCurrentPlan('free');
    }
  };

  const handleUpgrade = async (targetPlan, event) => {
    // Prevent event bubbling and default behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Prevent double clicks using ref
    if (isProcessingRef.current) {
      return;
    }

    if (!currentUser?.uid) {
      setError('Please sign in to upgrade');
      return;
    }

    // Don't allow upgrading to current plan
    if (currentPlan === targetPlan) {
      setError(`You are already on the ${targetPlan} plan`);
      return;
    }

    // Prevent multiple simultaneous requests
    if (loadingPlanId !== null) {
      return; // Already processing a plan
    }

    // Set processing flag and loading state
    isProcessingRef.current = true;
    setError('');
    setLoadingPlanId(targetPlan); // Set to the specific plan ID

    try {
      await redirectToCheckout(targetPlan);
      // User will be redirected to Stripe, so we don't need to handle success here
    } catch (err) {
      console.error('Error starting checkout:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setLoadingPlanId(null);
      isProcessingRef.current = false;
    }
  };

  if (!isOpen) return null;

  const plans = [
    {
      name: 'Free',
      id: 'free',
      price: '$0',
      period: 'month',
      description: 'Perfect for getting started',
      features: [
        { text: 'Analyze 3 videos total', enabled: true },
        { text: 'Only single YouTube URLs', enabled: true },
        { text: 'Max 100 comments per video', enabled: true },
        { text: 'Basic categorization only', enabled: true },
        { text: 'No favourites', enabled: false },
        { text: 'No channel analysis', enabled: false },
      ],
    },
    {
      name: 'Pro',
      id: 'pro',
      price: '$24',
      period: 'month',
      description: 'For serious content creators',
      features: [
        { text: 'Unlimited video analyses', enabled: true },
        { text: 'Search inside videos', enabled: true },
        { text: 'Favourites', enabled: true },
        { text: 'Recently Analyzed tab', enabled: true },
        { text: 'Full categorization', enabled: true },
        { text: 'Export comments (CSV)', enabled: true },
        { text: 'No channel analysis', enabled: false },
      ],
    },
    {
      name: 'Growth',
      id: 'growth',
      price: '$59',
      period: 'month',
      description: 'Everything you need to scale',
      features: [
        { text: 'Unlimited video analyses', enabled: true },
        { text: 'Search inside videos', enabled: true },
        { text: 'Favourites', enabled: true },
        { text: 'Recently Analyzed tab', enabled: true },
        { text: 'Full categorization', enabled: true },
        { text: 'Export comments (CSV)', enabled: true },
        { text: 'Channel analysis', enabled: true },
      ],
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div 
        className="rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#0c0c1a' }}
      >
        {/* Header */}
        <div 
          className="sticky top-0 px-6 py-4 flex items-center justify-between"
          style={{ backgroundColor: '#0c0c1a', borderBottom: '1.5px solid #2c2c3f' }}
        >
          <div>
            <h2 
              className="text-white"
              style={{
                fontFamily: "'Geist', sans-serif",
                fontSize: '24px',
                fontWeight: 600,
                lineHeight: '1.2',
                letterSpacing: '-0.24px'
              }}
            >
              Upgrade Your Plan
            </h2>
            <p 
              className="text-white text-opacity-70 mt-1"
              style={{
                fontFamily: "'Geist', sans-serif",
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '14px'
              }}
            >
              Choose the plan that's right for you
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:bg-white hover:bg-opacity-10"
            aria-label="Close"
          >
            <X size={24} className="text-white" />
          </button>
        </div>

        {/* Current Plan Badge */}
        <div 
          className="px-6 py-4"
          style={{ backgroundColor: '#1b1b2d', borderBottom: '1.5px solid #2c2c3f' }}
        >
          <div className="flex items-center gap-2">
            <Zap size={16} style={{ color: '#5432FF' }} /> {/* primary-500 */}
            <span 
              className="text-white"
              style={{
                fontFamily: "'Geist', sans-serif",
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              Current Plan: <span className="capitalize">{currentPlan}</span>
            </span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div 
            className="mx-6 mt-4 px-4 py-3"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '10px',
              color: '#fca5a5'
            }}
          >
            {error}
          </div>
        )}

        {/* Plans Grid */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const isCurrentPlan = currentPlan === plan.id;
              const canUpgrade = currentPlan !== plan.id && plan.id !== 'free';

              // Create a unique render function for each plan
              const renderPlanCard = () => {
                // Check if THIS specific plan is loading - use direct comparison
                const planId = plan.id;
                const isLoading = loadingPlanId === planId;

                return (
                  <div
                    key={planId}
                    className={`relative p-6 transition-all ${
                      isCurrentPlan
                        ? 'hover:shadow-lg'
                        : 'hover:shadow-lg'
                    }`}
                    style={{
                      backgroundColor: '#1b1b2d',
                      border: isCurrentPlan ? '1.5px solid #5432FF' : '1.5px solid #2c2c3f', // primary-500
                      borderRadius: '10px'
                    }}
                  >
                    {/* Current Plan Badge */}
                    {isCurrentPlan && (
                      <div 
                        className="absolute top-4 right-4 text-white text-xs font-semibold px-3 py-1"
                        style={{
                          backgroundColor: '#5432FF', // primary-500
                          borderRadius: '100px',
                          fontFamily: "'Geist Mono', monospace",
                          fontSize: '12px',
                          fontWeight: 400
                        }}
                      >
                        Current
                      </div>
                    )}

                    {/* Plan Header */}
                    <div className="mb-6">
                      <h3 
                        className="text-white mb-2"
                        style={{
                          fontFamily: "'Geist', sans-serif",
                          fontSize: '24px',
                          fontWeight: 600,
                          lineHeight: '1.2',
                          letterSpacing: '-0.24px'
                        }}
                      >
                        {plan.name}
                      </h3>
                      <div className="flex items-baseline gap-1 mb-2">
                        <span 
                          className="text-white"
                          style={{
                            fontFamily: "'Geist', sans-serif",
                            fontSize: '32px',
                            fontWeight: 600
                          }}
                        >
                          {plan.price}
                        </span>
                        {plan.period && (
                          <span 
                            className="text-white text-opacity-70"
                            style={{
                              fontFamily: "'Geist', sans-serif",
                              fontSize: '14px',
                              fontWeight: 400
                            }}
                          >
                            /{plan.period}
                          </span>
                        )}
                      </div>
                      {plan.description && (
                        <p 
                          className="text-white text-opacity-70"
                          style={{
                            fontFamily: "'Geist', sans-serif",
                            fontSize: '14px',
                            fontWeight: 400
                          }}
                        >
                          {plan.description}
                        </p>
                      )}
                    </div>

                    {/* Features List */}
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          {feature.enabled ? (
                            <Check size={16} className="text-green-400 mt-0.5 flex-shrink-0" />
                          ) : (
                            <Lock size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                          )}
                          <span
                            className={`${
                              feature.enabled ? 'text-white' : 'text-white text-opacity-40'
                            }`}
                            style={{
                              fontFamily: "'Geist', sans-serif",
                              fontSize: '14px',
                              fontWeight: 400,
                              lineHeight: '14px'
                            }}
                          >
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* Upgrade Button */}
                    {canUpgrade && (
                      <button
                        type="button"
                        data-plan-id={planId}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.nativeEvent.stopImmediatePropagation();
                          const clickedPlanId = e.currentTarget.getAttribute('data-plan-id');
                          handleUpgrade(clickedPlanId, e);
                        }}
                        disabled={loadingPlanId !== null}
                        className="w-full py-3 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        style={{
                          backgroundColor: '#5432FF', // primary-500
                          border: '1px solid white',
                          borderRadius: '10px',
                          fontFamily: "'Geist', sans-serif",
                          fontSize: '16px',
                          fontWeight: 600,
                          lineHeight: '16px',
                          letterSpacing: '-0.16px'
                        }}
                      >
                        {/* Only show Processing for the specific plan that's loading */}
                        {loadingPlanId === planId ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Processing...</span>
                          </>
                        ) : (
                          <>Upgrade to {plan.name}</>
                        )}
                      </button>
                    )}

                    {isCurrentPlan && (
                      <div 
                        className="w-full py-3 text-white text-opacity-70 font-semibold text-center"
                        style={{
                          backgroundColor: '#2c2c3f',
                          borderRadius: '10px',
                          fontFamily: "'Geist', sans-serif",
                          fontSize: '16px',
                          fontWeight: 600
                        }}
                      >
                        Current Plan
                      </div>
                    )}

                    {planId === 'free' && !isCurrentPlan && (
                      <div 
                        className="w-full py-3 text-white text-opacity-70 font-semibold text-center"
                        style={{
                          backgroundColor: '#2c2c3f',
                          borderRadius: '10px',
                          fontFamily: "'Geist', sans-serif",
                          fontSize: '16px',
                          fontWeight: 600
                        }}
                      >
                        Downgrade
                      </div>
                    )}
                  </div>
                );
              };

              return renderPlanCard();
            })}
          </div>
        </div>

        {/* Footer */}
        <div 
          className="px-6 py-4 text-center"
          style={{ backgroundColor: '#1b1b2d', borderTop: '1.5px solid #2c2c3f' }}
        >
          <p 
            className="text-white text-opacity-60"
            style={{
              fontFamily: "'Geist', sans-serif",
              fontSize: '12px',
              fontWeight: 400
            }}
          >
            All plans include secure payment processing via Stripe. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;

